import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useReaderStore } from '@/features/reader/stores/readerStore'
import { isPageReleased } from '@/features/reader/utils/toc'
import { api } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import type { BookAccess } from '@/types/api'

export function Bookmarks({ access }: { access: BookAccess }) {
  const page = useReaderStore(s => s.page)
  const go = useReaderStore(s => s.irParaPagina)
  const userId = useAuthStore(s => s.user?.id)
  const cache = useQueryClient()
  const key = ['bookmarks', userId, access.asset_id]
  const url = `/reader/books/${access.asset_id}/bookmarks`
  const query = useQuery({ queryKey: key, queryFn: async () => (await api.get<number[]>(url)).data })
  const update = useMutation({
    mutationFn: async ({ page, enabled }: { page: number; enabled: boolean }) => {
      await api.put(`${url}/${page}`, { enabled })
    },
    onSuccess: async () => { await cache.invalidateQueries({ queryKey: key }) },
  })
  const marked = query.data?.includes(page) ?? false
  return <section className="reader-study-tool" aria-label="Páginas favoritas">
    <button type="button" aria-pressed={marked}
      disabled={query.isPending || query.isError || update.isPending || (!marked && !isPageReleased(access, page))}
      onClick={() => update.mutate({ page, enabled: !marked })}>
      {marked ? 'Remover página dos favoritos' : 'Favoritar esta página'}
    </button>
    <details><summary>Páginas favoritas ({query.data?.length ?? 0})</summary>
      <p>Salvas na sua conta.</p>
      {query.isPending && <p role="status">Carregando favoritos…</p>}
      {query.data?.length === 0 && <p>Nenhuma página favorita neste livro.</p>}
      <ul>{query.data?.map(number => <li key={number}>
        <button type="button" disabled={!isPageReleased(access, number)} onClick={() => go(number, access.page_count ?? undefined)}>
          Página {number}{!isPageReleased(access, number) && ' — ainda não liberada'}
        </button>
        <button type="button" disabled={update.isPending} aria-label={`Remover favorito da página ${number}`}
          onClick={() => update.mutate({ page: number, enabled: false })}>Remover</button>
      </li>)}</ul>
    </details>
    {update.isPending && <p role="status">Salvando favoritos…</p>}
    {(query.isError || update.isError) && <p role="alert">Não foi possível atualizar os favoritos.
      <button type="button" disabled={update.isPending} onClick={() => {
        if (update.isError && update.variables) update.mutate(update.variables)
        else void query.refetch()
      }}>Tentar novamente</button>
    </p>}
  </section>
}
